import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { propertiesForSale, propertiesForRent } from '../data/properties';

function PropertyDetailsPage() {
    const { id } = useParams();

    // Find property in either list
    const property = [...propertiesForSale, ...propertiesForRent].find(p => p.id === id || p.id === parseInt(id));

    if (!property) {
        return (
            <div className="flex flex-col min-h-screen font-sans bg-white">
                <Navbar />
                <main className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="text-4xl font-light text-gray-900 mb-4">Property Not Found</h1>
                        <Link to="/" className="text-accent hover:underline">Return Home</Link>
                    </div>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen font-sans bg-white">
            <Navbar />

            <main className="flex-1 pb-20">
                {/* Hero Image */}
                <div className="relative h-[60vh] w-full">
                    <img
                        src={property.img}
                        alt={property.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40" />
                    <div className="absolute bottom-0 left-0 w-full p-8 bg-gradient-to-t from-black/80 to-transparent">
                        <div className="max-w-6xl mx-auto text-white">
                            <div className="flex flex-col md:flex-row justify-between items-end gap-4">
                                <div>
                                    <span className="inline-block px-3 py-1 bg-accent rounded-full text-sm font-medium mb-4">
                                        {property.type}
                                    </span>
                                    <h1 className="text-4xl md:text-5xl font-light mb-2" style={{ fontFamily: 'Roboto, sans-serif' }}>
                                        {property.title}
                                    </h1>
                                    <p className="text-xl opacity-90">{property.location}</p>
                                </div>
                                <div className="text-3xl md:text-4xl font-semibold">
                                    {property.price}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-4 py-12">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Main Content */}
                        <div className="lg:col-span-2 space-y-12">
                            {/* Key Features */}
                            <section>
                                <h2 className="text-2xl font-light text-gray-900 mb-6 border-b pb-4">Property Overview</h2>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                                        <div className="text-gray-500 text-sm mb-1">Bedrooms</div>
                                        <div className="text-xl font-semibold text-gray-900">{property.beds || 'Studio'}</div>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                                        <div className="text-gray-500 text-sm mb-1">Bathrooms</div>
                                        <div className="text-xl font-semibold text-gray-900">{property.baths}</div>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                                        <div className="text-gray-500 text-sm mb-1">Size</div>
                                        <div className="text-xl font-semibold text-gray-900">{property.sqft} sqft</div>
                                    </div>
                                    <div className="p-4 bg-gray-50 rounded-xl text-center">
                                        <div className="text-gray-500 text-sm mb-1">Availability</div>
                                        <div className="text-xl font-semibold text-green-600">{property.availability || 'Available'}</div>
                                    </div>
                                </div>
                            </section>

                            {/* Description */}
                            <section>
                                <h2 className="text-2xl font-light text-gray-900 mb-6 border-b pb-4">Description</h2>
                                <div className="prose max-w-none text-gray-600 leading-relaxed">
                                    <p>{property.description || 'No description available for this property.'}</p>
                                </div>
                            </section>

                            {/* Gallery */}
                            {property.images && property.images.length > 0 && (
                                <section>
                                    <h2 className="text-2xl font-light text-gray-900 mb-6 border-b pb-4">Gallery</h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {property.images.map((img, idx) => (
                                            <div key={idx} className="relative aspect-[4/3] rounded-xl overflow-hidden group cursor-pointer">
                                                <img
                                                    src={img}
                                                    alt={`${property.title} - ${idx + 1}`}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                    loading="lazy"
                                                />
                                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* Additional Details */}
                            <section>
                                <h2 className="text-2xl font-light text-gray-900 mb-6 border-b pb-4">Details</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-500">Owner</span>
                                        <span className="font-medium">{property.owner || 'N/A'}</span>
                                    </div>
                                    <div className="flex justify-between py-2 border-b border-gray-100">
                                        <span className="text-gray-500">NOC Signed</span>
                                        <span className="font-medium">{property.nocSigned || 'N/A'}</span>
                                    </div>
                                    {property.cheques && (
                                        <div className="flex justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-500">Cheques</span>
                                            <span className="font-medium">{property.cheques}</span>
                                        </div>
                                    )}
                                    {property.furnished !== undefined && (
                                        <div className="flex justify-between py-2 border-b border-gray-100">
                                            <span className="text-gray-500">Furnished</span>
                                            <span className="font-medium">{property.furnished ? 'Yes' : 'No'}</span>
                                        </div>
                                    )}
                                </div>
                            </section>
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-1">
                            <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-200 sticky top-24">
                                <h3 className="text-xl font-light text-gray-900 mb-6">Interested in this property?</h3>
                                <div className="space-y-4">
                                    <a
                                        href="https://wa.me/971505606877"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center justify-center w-full py-4 bg-[#25D366] text-white rounded-lg font-semibold hover:bg-[#128C7E] transition-colors"
                                    >
                                        WhatsApp Inquiry
                                    </a>
                                    <a
                                        href="tel:+971505606877"
                                        className="flex items-center justify-center w-full py-4 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors"
                                    >
                                        Call Agent
                                    </a>
                                    <a
                                        href="mailto:info@alphaahead.estate"
                                        className="flex items-center justify-center w-full py-4 bg-white border-2 border-black text-black rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                                    >
                                        Email Inquiry
                                    </a>
                                </div>
                                <p className="text-xs text-center text-gray-500 mt-6">
                                    Reference ID: {property.id}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
}

export default PropertyDetailsPage;
